import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Auth is carried exclusively by httpOnly cookies (`access_token`, `refresh_token`)
// set by the backend on login. We never read or send the token from JS, which
// prevents XSS from exfiltrating the session.
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  // Client-side abort after 30 s. Cloudflare kills origin requests at ~100 s
  // and floods dispatchers with "invalid response" 520 errors. Aborting our
  // side early keeps the browser's connection pool from piling up dead
  // sockets that later reappear as spurious errors.
  timeout: 30000,
});

// Best-effort cleanup of legacy tokens that older builds stored in
// localStorage. Safe no-op if the key is absent — a SecurityError from
// disabled storage (private mode, quotas) is expected and non-fatal.
try {
  if (typeof window !== "undefined") localStorage.removeItem("np_token");
} catch (err) {
  if (process.env.NODE_ENV !== "production") console.debug("legacy token cleanup skipped:", err?.message || err);
}

const SESSION_REPLACED_DETAIL = "Session replaced by another device";
const INACTIVITY_LOGOUT_DETAIL = "Sesión cerrada por inactividad";

// Toast+redirect exactly once when the backend tells us another device took
// over the same account. Guarded so we don't spam the user with duplicate
// toasts across the many in-flight requests that fail at the same time.
let _sessionKickedShown = false;
function _handleSessionReplaced() {
  if (_sessionKickedShown) return;
  _sessionKickedShown = true;
  try {
    toast.error("Sesión cerrada porque iniciaste sesión en otro dispositivo", {
      duration: 8000,
    });
  } catch { /* toast provider not mounted yet */ }
  // Give the toast a beat to render, then hard-navigate to /login. Hard
  // reload also clears any stale client caches / event listeners.
  setTimeout(() => {
    _sessionKickedShown = false;
    if (typeof window !== "undefined") window.location.assign("/login");
  }, 900);
}

// Same pattern as `_handleSessionReplaced` but for the 2h-inactivity
// auto-offline sweep (see backend `_auto_offline_stale_drivers`). A
// chofer whose phone was left idle > 2h is force-logged-out so a
// stale LIBRE pill can't sit on the map forever.
let _inactivityKickedShown = false;
function _handleInactivityLogout() {
  if (_inactivityKickedShown) return;
  _inactivityKickedShown = true;
  try {
    toast.error("Sesión cerrada por inactividad (más de 2 horas). Inicia sesión de nuevo.", {
      duration: 8000,
    });
  } catch { /* toast provider not mounted yet */ }
  setTimeout(() => {
    _inactivityKickedShown = false;
    if (typeof window !== "undefined") window.location.assign("/login");
  }, 900);
}

// When the browser can't reach the API at all (DNS failure, offline, mobile
// network drop, TLS handshake broken), axios rejects with `err.message =
// "Network Error"` and NO `err.response`. Bubbling that raw English string up
// to individual toasts is confusing and doesn't tell the user what to do. We
// swallow the toast noise at the interceptor level and show ONE friendly,
// actionable message per stretch of connectivity trouble.
let _netErrorShownAt = 0;
function _handleNetworkError() {
  const now = Date.now();
  // Debounce: only re-toast every 30s so a flurry of failed retries doesn't
  // stack toasts on top of each other.
  if (now - _netErrorShownAt < 30000) return;
  _netErrorShownAt = now;
  try {
    toast.error("Sin conexión con el servidor. Revisa tu Wi-Fi o datos móviles y recarga la página.", {
      duration: 6000,
    });
  } catch { /* toast provider not mounted yet */ }
}

// Auto-refresh on 401: try once to rotate the access cookie via
// POST /auth/refresh, then replay the original request. If refresh also
// fails, let the original 401 bubble up so the app redirects to /login.
let _refreshInFlight = null;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    const url = original?.url || "";
    const detail = error?.response?.data?.detail;
    const isAuthCall =
      url.includes("/auth/login") ||
      url.includes("/auth/google") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout") ||
      url.includes("/auth/me");
    // Network-level failure (no HTTP response at all): show a friendly banner
    // and rethrow. Skip for /auth/me on cold boot — that one is expected to
    // fail silently on iOS Safari when the app opens with no connectivity;
    // the AuthContext will fall back to the login screen.
    if (!error?.response && !url.includes("/auth/me")) {
      // Retry once for timeouts / connection resets on GET requests — these
      // are almost always Cloudflare edge hiccups and a 750 ms breather is
      // enough to get a good response next time without hammering origin.
      const method = (original?.method || "").toLowerCase();
      const retriable = method === "get" && (error?.code === "ECONNABORTED" || /Network Error/i.test(error?.message || ""));
      if (retriable && original && !original._retriedTx) {
        original._retriedTx = true;
        await new Promise((r) => setTimeout(r, 750));
        return api(original);
      }
      _handleNetworkError();
      return Promise.reject(error);
    }
    // Cloudflare origin errors (502 Bad Gateway / 520 Origin invalid /
    // 522 timeout / 524 timeout). Retry GET once after a small delay so
    // background polls don't torch the dispatcher UI with error toasts.
    if (
      [502, 520, 522, 524].includes(status) &&
      original && !original._retriedCf &&
      (original.method || "").toLowerCase() === "get"
    ) {
      original._retriedCf = true;
      await new Promise((r) => setTimeout(r, 750));
      return api(original);
    }
    // Short-circuit: if we already know the server replaced our session
    // (from get_current_user or refresh), do the user-visible handling here
    // and skip the retry loop.
    if (status === 401 && detail === SESSION_REPLACED_DETAIL) {
      _handleSessionReplaced();
      return Promise.reject(error);
    }
    // Same short-circuit for the 2h auto-offline sweep. Refreshing the
    // token would just 401 again — the server rotated our sid — so we
    // skip the retry and take the user to /login directly.
    if (status === 401 && detail === INACTIVITY_LOGOUT_DETAIL) {
      _handleInactivityLogout();
      return Promise.reject(error);
    }
    if (status === 401 && original && !original._retried && !isAuthCall) {
      original._retried = true;
      try {
        _refreshInFlight = _refreshInFlight || api.post("/auth/refresh");
        await _refreshInFlight;
        _refreshInFlight = null;
        return api(original);
      } catch (e) {
        _refreshInFlight = null;
        // If refresh itself failed with "session replaced", surface the
        // right message now instead of a generic 401.
        if (e?.response?.data?.detail === SESSION_REPLACED_DETAIL) {
          _handleSessionReplaced();
        } else if (e?.response?.data?.detail === INACTIVITY_LOGOUT_DETAIL) {
          _handleInactivityLogout();
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  // No HTTP response at all → transport/DNS/offline. The raw `Network Error`
  // string is confusing (English, no action to take). Translate to Spanish
  // with a concrete next step so the driver knows what to do.
  if (err && !err.response) {
    return "Sin conexión con el servidor. Revisa tu internet e inténtalo de nuevo.";
  }
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
